import { InputField } from "@/components/input-field/InputField";
import { CustomPopover } from "@/components/popover/CustomPopover";
import {
  AsyncAutocompleteSelect,
  Option,
} from "@/components/select/AsyncAutocompleteSelect";
import { SelectComponent } from "@/components/select/Select";
import {
  getOptionsHandler,
  loadOptionsHandler,
} from "@/pages/requests/cash/schema/handler-functions";
import {
  EntityStatusOption,
  getStatusByEntity,
} from "@/services/entity-status.service";
import {
  getGranosProductivos,
  type GranoProductivo,
} from "@/services/productive-entities.service";
import { EntityTypes } from "@/constants/enums";
import { AppColors } from "@/styles/colors";
import {
  Badge,
  Box,
  Button,
  Field,
  Flex,
  Grid,
  GridItem,
  HStack,
  Text,
} from "@chakra-ui/react";
import { Filter } from "lucide-react";
import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Control, FieldValues, UseFormHandleSubmit } from "react-hook-form";

interface Props {
  handleSubmit: UseFormHandleSubmit<FieldValues>;
  openFilters: boolean;
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  submitFilters: (event: FieldValues) => void;
  clearFilters: () => void;
  control: Control<FieldValues>;
  disableFilters?: boolean;
  loadingFilters?: boolean;
  filtersApplied?: boolean;
  disableClear?: boolean;
  disableApply?: boolean;
}

export const ProductPlanificationFilter = ({
  openFilters,
  disableFilters,
  loadingFilters,
  filtersApplied,
  disableClear,
  control,
  disableApply,
  submitFilters,
  handleSubmit,
  setOpenFilters,
  clearFilters,
}: Props) => {
  const [isActive, setIsActive] = useState<boolean>(false);
  useEffect(() => {
    setIsActive(true);
  }, []);

  const [statusOptions, setStatusOptions] = useState(
    [] as EntityStatusOption[],
  );
  const [loadingStatusOptions, setLoadingStatusOptions] =
    useState<boolean>(false);

  const fetchStatusOptions = async () => {
    setLoadingStatusOptions(true);
    const response = await getStatusByEntity({
      Id: [EntityTypes.PlanificacionProductiva],
    });
    setStatusOptions(response ?? []);
    setLoadingStatusOptions(false);
  };

  const timeoutRef = useRef(null as NodeJS.Timeout | null);
  const [loadingAccounts, setLoadingAccounts] = useState<boolean>(false);

  const getAccountOptions = async (inputValue: string) => {
    return getOptionsHandler({ inputValue });
  };

  const loadAccountOptions = (inputValue: string): Promise<Option[]> => {
    return loadOptionsHandler({
      timeoutRef,
      inputValue,
      setLoadingPersons: setLoadingAccounts,
      getOptions: getAccountOptions,
    });
  };

  const [cultivos, setCultivos] = useState([] as GranoProductivo[]);
  const [loadingCultivos, setLoadingCultivos] = useState<boolean>(false);

  const fetchCultivos = async () => {
    setLoadingCultivos(true);
    const response = await getGranosProductivos();
    setCultivos(response ?? []);
    setLoadingCultivos(false);
  };

  useEffect(() => {
    if (isActive) {
      fetchCultivos();
      fetchStatusOptions();
    }
  }, [isActive]);

  const cultivoItems = useMemo(
    () =>
      cultivos?.map((c) => ({
        label: c.nombre,
        value: String(c.id),
      })),
    [cultivos],
  );

  return (
    <Flex width="100%" paddingY="10px" justifyContent="flex-end">
      <HStack gap="10">
        <CustomPopover
          open={openFilters}
          onOpenChange={(e) => setOpenFilters(e.open)}
          triggerComponent={
            <Box position="relative" display="inline-block">
              <Button
                variant="outline"
                paddingX="12px"
                height="32px"
                disabled={disableFilters || loadingFilters}
              >
                <Filter size={16} />
                <Text fontWeight={600} fontSize="14px">
                  Filtrar
                </Text>
              </Button>
              {filtersApplied && (
                <Badge
                  position="absolute"
                  top="-4px"
                  right="-4px"
                  bg="red.500"
                  color="white"
                  fontSize="10px"
                  borderRadius="full"
                  px="2"
                  py="0.5"
                >
                  !
                </Badge>
              )}
            </Box>
          }
          bodyComponent={
            <Flex
              as="form"
              flexDir="column"
              padding="25px"
              gap="15px"
              onSubmit={handleSubmit(submitFilters)}
            >
              <Flex alignItems="center" justifyContent="space-between">
                <Text fontWeight={600} fontSize="20px">
                  Filtrar
                </Text>

                <Button
                  variant="plain"
                  color={AppColors.BLUE_TEXT}
                  fontWeight={600}
                  onClick={clearFilters}
                  disabled={loadingFilters || disableClear || disableFilters}
                  loading={loadingFilters}
                >
                  Limpiar filtros
                </Button>
              </Flex>

              <Grid templateColumns="repeat(2, 1fr)" gap={4} alignItems="end">
                <GridItem maxW="300px">
                  <Field.Root>
                    <Field.Label>
                      <Text fontWeight={600}>Cuenta origen</Text>
                    </Field.Label>
                    <AsyncAutocompleteSelect
                      control={control}
                      loadOptions={loadAccountOptions}
                      name="cuentaOrigen"
                      placeholder="Buscar..."
                      isLoading={loadingAccounts}
                      disabled={
                        disableFilters || loadingFilters || loadingAccounts
                      }
                      closeMenuOnSelect
                      isClearable
                      triggerWithLength
                    />
                  </Field.Root>
                </GridItem>

                <GridItem maxW="300px">
                  <SelectComponent
                    name="conDesvioHectareas"
                    items={[
                      { label: "Con desvío", value: true },
                      { label: "Sin desvío", value: false },
                    ]}
                    labelText="Desvío hectáreas"
                    placeholder="Seleccionar"
                    control={control}
                    minHeight="40px"
                    disabled={disableFilters || loadingFilters}
                  />
                </GridItem>

                <GridItem maxW="300px">
                  <InputField
                    inputName="hectareasPropias"
                    labelText="Hectáreas propias"
                    placeholder="Ingresar"
                    control={control}
                    disabled={disableFilters || loadingFilters}
                  />
                </GridItem>

                <GridItem maxW="300px">
                  <InputField
                    inputName="hectareasAlquiladas"
                    labelText="Hectáreas alquiladas"
                    placeholder="Ingresar"
                    control={control}
                    disabled={disableFilters || loadingFilters}
                  />
                </GridItem>

                <GridItem colSpan={2}>
                  <SelectComponent
                    name="cultivos"
                    items={cultivoItems}
                    labelText="Cultivos"
                    placeholder="Seleccionar"
                    control={control}
                    fullWidth
                    loading={loadingCultivos}
                    disabled={disableFilters || loadingFilters || loadingCultivos}
                    multiple
                    menuMaxHeight="250px"
                  />
                </GridItem>

                <GridItem colSpan={2}>
                  <SelectComponent
                    name="estados"
                    items={statusOptions?.map((status) => ({
                      label: status?.nombre,
                      value: status?.ids?.join("|"),
                    }))}
                    labelText="Estados"
                    placeholder="Seleccionar"
                    control={control}
                    fullWidth
                    disabled={
                      disableFilters || loadingFilters || loadingStatusOptions
                    }
                    loading={loadingStatusOptions || loadingFilters}
                    multiple
                    menuMaxHeight="250px"
                  />
                </GridItem>
              </Grid>

              <Button
                bgColor={AppColors.GREEN_PRIMARY}
                type="submit"
                loading={loadingFilters}
                disabled={loadingFilters || disableFilters || disableApply}
              >
                Aplicar filtros
              </Button>
            </Flex>
          }
        />
      </HStack>
    </Flex>
  );
};
